// Module: app
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §7, §8.3, §9.3
//
// The orchestrator. The only module that touches the DOM, and the only one that touches browser
// storage. Everything below it is pure or a narrow adapter; this is the glue, which is exactly
// where the methodology says bugs live.

import type { VaultSession, LockReason, Adoption } from "../session_2026-08-27/session.js"
import type { Entry } from "../vault-store_2026-08-27/vault-store.js"
import {
  generate as generatePassphrase,
  meetsFloor,
  MIN_PASSWORD_LENGTH,
} from "../passphrase_2026-08-27/passphrase.js"
import type { CheckpointStore } from "../remote-storage_2026-08-27/remote-storage.js"
import type { KdfParams } from "../key-hierarchy_2026-08-27/key-hierarchy.js"
import { sealLocal, openLocal, generateKdfParams } from "../key-hierarchy_2026-08-27/key-hierarchy.js"

export type ScreenName =
  | "loading"
  | "welcome"
  | "enrolling"
  | "credentials"
  | "locked"
  | "adopt"
  | "vault"
  | "recovery"
  | "start-over"
  | "vaults"

export type ClipboardLike = {
  writeText(text: string): Promise<void>
  readText?(): Promise<string>
}

/**
 * What this machine knows so it can open a vault without the hardware key. Not uploaded.
 *
 * A set, not a single record. One credential now holds several named vaults (spec §2.2), and the
 * single-record version is what left a machine adopted to vault A with no route to vault B at
 * all: the app read "some adoption exists" as "the adoption you want exists".
 */
export type AdoptionStore = {
  list(): Promise<Adoption[]>
  get(vaultId: string): Promise<Adoption | null>
  set(adoption: Adoption): Promise<void>
  delete(vaultId: string): Promise<void>
  clear(): Promise<void>
}

/**
 * The vault names this machine has seen, sealed under the password (§3.6 pattern).
 *
 * Sealed rather than stored plainly because the adversary this system has is someone holding the
 * laptop (§1.4) — and reading "crypto" off an unlocked list is exactly the disclosure that naming
 * vaults would otherwise introduce. It is a convenience: the name is always retypeable, and a
 * machine with no cache still opens any vault through the key.
 */
export type SealedNames = { sealed: Uint8Array; kdf: KdfParams }

export type NameCacheStore = {
  read(): Promise<SealedNames[]>
  write(entries: SealedNames[]): Promise<void>
  clear(): Promise<void>
}

export type AppDeps = {
  session: VaultSession
  adoptions?: AdoptionStore
  names?: NameCacheStore
  clipboard?: ClipboardLike
  clock?: () => number
  clipboardClearMs?: number
  saveDebounceMs?: number
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">
}

export interface AppHandle {
  readonly screen: ScreenName
  destroy(): void
}

class SessionLikeError extends Error {
  readonly code = "WhenLocked"
}

export const CLIPBOARD_CLEAR_MS = 30_000
export const SAVE_DEBOUNCE_MS = 2_000
const SEEN_VAULT_KEY = "vault.seen"
const CREDENTIAL_KEY = "vault.credentialId"
/** Public-key material, and not secret — the same reasoning as the credential id below. */
const CREDENTIAL_PK_KEY = "vault.credentialPublicKey"
const VAULT_ID_KEY = "vault.id"
/**
 * Whether the unlock screen shows the password. Records a preference, never a value — nothing
 * secret goes into storage. Absent means "show it", so the friendly default survives a browser
 * that refuses storage entirely.
 */
const REVEAL_KEY = "vault.reveal"

const b64u = (bytes: Uint8Array): string => {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "")
}

const unb64u = (text: string): Uint8Array | undefined => {
  try {
    const binary = atob(text.replace(/-/gu, "+").replace(/_/gu, "/"))
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i)
    return out
  } catch {
    return undefined
  }
}

// --- tiny DOM helper -------------------------------------------------------------------------
// textContent everywhere, never innerHTML: entry titles and notes are attacker-influenced the
// moment a vault is imported from elsewhere.

type Attrs = Record<string, string | boolean | number | undefined>

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue
    if (key === "class") node.className = String(value)
    else if (value === true) node.setAttribute(key, "")
    else node.setAttribute(key, String(value))
  }
  for (const child of children) {
    node.append(typeof child === "string" ? document.createTextNode(child) : child)
  }
  return node
}

// --- the eye toggle --------------------------------------------------------------------------
// Spec §0: the default is to show the user what they are typing. Masking is a choice they make,
// not one we make for them, so every secret field carries its own control.

const SVG_NS = "http://www.w3.org/2000/svg"

/**
 * `el` cannot build these: `document.createElement` puts nodes in the HTML namespace, where an
 * <svg> is inert. Everything is a presentation attribute rather than a `style` attribute, so the
 * strict CSP in spec §8.3 (`style-src 'self'`, no `unsafe-inline`) still holds.
 */
function svgIcon(kind: "eye" | "eye-off"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg")
  for (const [k, v] of Object.entries({
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "aria-hidden": "true",
    focusable: "false",
  })) {
    svg.setAttribute(k, v)
  }
  const path = (d: string): void => {
    const p = document.createElementNS(SVG_NS, "path")
    p.setAttribute("d", d)
    svg.append(p)
  }
  const circle = (): void => {
    const c = document.createElementNS(SVG_NS, "circle")
    c.setAttribute("cx", "12")
    c.setAttribute("cy", "12")
    c.setAttribute("r", "3")
    svg.append(c)
  }
  if (kind === "eye") {
    path("M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z")
    circle()
  } else {
    path("M10.6 6.2A9.9 9.9 0 0 1 12 6c6.5 0 10 6 10 6a17.7 17.7 0 0 1-3.4 4.1")
    path("M6.6 6.9A17.6 17.6 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.1-1.4")
    path("M9.9 9.9a3 3 0 0 0 4.2 4.2")
    path("M3 3l18 18")
  }
  return svg
}

export type SecretField = {
  /** The wrapper to place in the DOM. Contains the input and its toggle. */
  readonly wrap: HTMLElement
  readonly input: HTMLInputElement
  setRevealed(next: boolean): void
}

/**
 * A password input with an eye button pinned to its right edge.
 *
 * The toggle mutates `input.type` **in place and never calls `render()`**. This is load-bearing,
 * not a micro-optimisation: `render()` rebuilds a screen from stored state, so re-rendering
 * throws away whatever the user has typed but not yet saved. The editor's old Reveal button did
 * exactly that — pressing it mid-edit discarded every keystroke since the modal opened, and
 * blanked the whole form on a new entry.
 */
function secretField(
  attrs: Attrs,
  opts: { revealed: boolean; onToggle?: (revealed: boolean) => void } = { revealed: true },
): SecretField {
  const input = el("input", { ...attrs, type: opts.revealed ? "text" : "password" })
  const button = el("button", {
    type: "button",
    class: "eye",
    id: attrs["id"] === undefined ? undefined : `${String(attrs["id"])}-eye`,
    "aria-controls": attrs["id"] === undefined ? undefined : String(attrs["id"]),
  })

  let revealed = opts.revealed

  const paint = (): void => {
    input.setAttribute("type", revealed ? "text" : "password")
    input.classList.toggle("mono", revealed)
    button.setAttribute("aria-pressed", revealed ? "true" : "false")
    // The label says what pressing it will do, which is what a screen reader user needs.
    const label = revealed ? "Hide password" : "Show password"
    button.setAttribute("aria-label", label)
    button.setAttribute("title", label)
    button.replaceChildren(svgIcon(revealed ? "eye-off" : "eye"))
  }
  paint()

  button.addEventListener("click", (event) => {
    // Every one of these sits inside a <label for="…">, whose activation behaviour would
    // otherwise forward this click on to the input as well.
    event.preventDefault()
    event.stopPropagation()
    revealed = !revealed
    paint()
    input.focus()
    opts.onToggle?.(revealed)
  })

  return {
    wrap: el("div", { class: "secret-field" }, [input, button]),
    input,
    setRevealed(next: boolean): void {
      if (next === revealed) return
      revealed = next
      paint()
    },
  }
}

/** One sentence a person can act on. The distinctions are kept: these are different problems. */
export function describeError(error: unknown): string {
  const code = (error as { code?: string } | null)?.code
  switch (code) {
    case "UnwrapFailed":
      return "That passphrase does not open this vault. Check it and try again."
    // Nothing throws this any more — the floor became a warning at the create screen (spec §0).
    // Kept as a safety net so an older module or a stale bundle cannot surface a raw error string.
    case "WeakPassphrase":
      return "That password was refused. Check it and try again."
    // The code is a misnomer kept from when this was its only cause. WebAuthn returns
    // NotAllowedError for a deliberately indistinguishable set of failures - dismissal, timeout,
    // and "this authenticator holds no usable credential for this relying party" - so a page
    // cannot be probed for which. Naming only one of them, as this did, sends the user to look in
    // the wrong place: on iOS a key the platform will not choose from reads exactly like a
    // dismissed prompt.
    case "UserCancelled":
      return (
        "The security key step did not finish. It was dismissed or timed out, or this key holds " +
        "no credential this browser would use for the vault \u2014 the browser does not say " +
        "which. Try again and hold the key steady."
      )
    case "Timeout":
      return "The security key did not respond in time. Try again."
    case "AuthenticatorError":
      return (
        "Your security key answered, but this browser did not pass on everything the vault " +
        "needs. If you are on an iPhone or iPad, try the same step on a computer."
      )
    case "NoCredential":
      return "No enrolled security key was found. Insert the key you used to create this vault."
    case "PrfUnavailable":
      return (
        "That credential cannot unlock this vault \u2014 it does not support the extension the " +
        "vault needs. If the browser offered you a choice, pick your security key rather than a " +
        "saved passkey, and try again."
      )
    case "NoMatchingSlot":
      return (
        "That credential is not enrolled for this vault. If the browser showed a list, a " +
        "different passkey was chosen \u2014 try again and pick the security key you used to " +
        "create this vault."
      )
    case "VaultNotFound":
      return "No vault exists for that security key."
    case "VaultExists":
      return "That security key already has a vault. Unlock it instead of creating another."
    case "RecordTampered":
      return "The vault's index failed its integrity check. It may have been altered. Nothing was opened."
    case "RollbackDetected":
      return "The server returned an older copy of this vault than this device has already seen. Nothing was opened."
    case "ConflictDetected":
      return "This vault changed somewhere else while you were editing. Reload before saving."
    case "CredentialExists":
      return "That security key is already enrolled for this vault."
    case "RateLimited":
      return "Too many attempts. Wait a minute and try again."
    case "NetworkError":
      return "Could not reach the vault service. Check your connection."
    default:
      return error instanceof Error && error.message ? error.message : "Something went wrong."
  }
}

// --- browser storage, which only this module may touch (spec §9.3) ----------------------------

/**
 * Where the sealed hardware secret lives. IndexedDB rather than localStorage because it stores
 * bytes directly, and because it is the same origin-scoped store the checkpoints already use.
 *
 * Losing it is not losing the vault — it costs one hardware-key ceremony to adopt the machine
 * again — but it is the difference between opening with a password and needing the key.
 */
export function createIndexedDbAdoption(dbName = "vault-adoption"): AdoptionStore {
  const STORE = "adoption"

  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(STORE)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })

  const run = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> =>
    open().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const request = fn(db.transaction(STORE, mode).objectStore(STORE))
          request.onsuccess = () => resolve(request.result as T)
          request.onerror = () => reject(request.error)
        }),
    )

  type Stored = { vaultId: string; slotId: string; sealed: ArrayBuffer }
  const revive = (stored: Stored): Adoption => ({
    vaultId: stored.vaultId,
    slotId: stored.slotId,
    sealed: new Uint8Array(stored.sealed),
  })

  return {
    async list() {
      // Records written by the single-adoption version are keyed "current" rather than by vault
      // id. They still revive correctly, and the next `set` rekeys them.
      const all = await run<Stored[]>("readonly", (s) => s.getAll())
      return all.filter((stored) => stored?.vaultId !== undefined).map(revive)
    },
    async get(vaultId) {
      const stored = await run<Stored | undefined>("readonly", (s) => s.get(vaultId))
      return stored ? revive(stored) : null
    },
    async set(adoption) {
      await run("readwrite", (s) =>
        s.put(
          {
            vaultId: adoption.vaultId,
            slotId: adoption.slotId,
            sealed: adoption.sealed.buffer.slice(
              adoption.sealed.byteOffset,
              adoption.sealed.byteOffset + adoption.sealed.byteLength,
            ),
          },
          adoption.vaultId,
        ),
      )
    },
    async delete(vaultId) {
      await run("readwrite", (s) => s.delete(vaultId))
    },
    async clear() {
      await run("readwrite", (s) => s.clear())
    },
  }
}

/** The sealed name cache, beside the adoptions in the same origin-scoped database. */
export function createIndexedDbNameCache(dbName = "vault-adoption"): NameCacheStore {
  const STORE = "adoption"
  const KEY = "\u0000names"

  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(STORE)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })

  const run = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> =>
    open().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const request = fn(db.transaction(STORE, mode).objectStore(STORE))
          request.onsuccess = () => resolve(request.result as T)
          request.onerror = () => reject(request.error)
        }),
    )

  // One entry per password, not per vault: revealing the list then costs one PBKDF2 per distinct
  // password rather than one per vault. Vaults under a password you have not typed stay sealed,
  // which is why the list says how many it could not open rather than pretending they are absent.
  return {
    async read() {
      const stored = await run<{ entries: { sealed: ArrayBuffer; kdf: KdfParams }[] } | undefined>(
        "readonly",
        (s) => s.get(KEY),
      )
      if (!stored?.entries) return []
      return stored.entries.map((e) => ({ sealed: new Uint8Array(e.sealed), kdf: e.kdf }))
    },
    async write(entries) {
      await run("readwrite", (s) =>
        s.put(
          {
            entries: entries.map((e) => ({
              sealed: e.sealed.buffer.slice(
                e.sealed.byteOffset,
                e.sealed.byteOffset + e.sealed.byteLength,
              ),
              kdf: e.kdf,
            })),
          },
          KEY,
        ),
      )
    },
    async clear() {
      await run("readwrite", (s) => s.delete(KEY))
    },
  }
}

export function createIndexedDbCheckpoints(dbName = "vault-checkpoints"): CheckpointStore {
  const STORE = "revisions"

  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE)
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })

  const run = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> =>
    open().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const request = fn(db.transaction(STORE, mode).objectStore(STORE))
          request.onsuccess = () => resolve(request.result as T)
          request.onerror = () => reject(request.error)
        }),
    )

  return {
    async get(vaultId) {
      // Stored as a decimal string: a revision is a uint64 and IndexedDB would round a number.
      const value = await run<string | undefined>("readonly", (s) => s.get(vaultId))
      return value === undefined ? null : BigInt(value)
    },
    async set(vaultId, revision) {
      await run("readwrite", (s) => s.put(revision.toString(10), vaultId))
    },
  }
}

// --- the app ----------------------------------------------------------------------------------

export function mount(root: HTMLElement, deps: AppDeps): AppHandle {
  const clipboard =
    deps.clipboard ?? (globalThis as { navigator?: { clipboard?: ClipboardLike } }).navigator?.clipboard
  const clock = deps.clock ?? (() => Date.now())
  const clipboardClearMs = deps.clipboardClearMs ?? CLIPBOARD_CLEAR_MS
  const saveDebounceMs = deps.saveDebounceMs ?? SAVE_DEBOUNCE_MS
  const storage =
    deps.storage ?? (globalThis as { localStorage?: AppDeps["storage"] }).localStorage ?? null

  let screen: ScreenName = "loading"
  let error: string | null = null
  let warning: string | null = null
  let toast: string | null = null
  let query = ""
  let editing: Entry | "new" | null = null
  let revealed = false
  let noticeHolder: HTMLElement | null = null
  /** The exact password the weak-password warning was last shown for. See the create handler. */
  let warnedFor: string | null = null
  let credentials: { recoveryPhrase: string; vaultId: string; name: string } | null = null
  let confirmedCode = false
  /** The vault this screen is about. Null when several are adopted and none is chosen yet. */
  let adoption: Adoption | null = null
  let adoptionList: Adoption[] = []
  /** Names unsealed by the password typed on the vaults screen. In memory only, dropped on lock. */
  let vaultNames = new Map<string, string>()
  /** The password that opened the list, so choosing a vault does not ask for it twice. */
  let listPassword: string | null = null
  const adoptions = deps.adoptions ?? createIndexedDbAdoption()
  let busy = false

  let saveTimer: ReturnType<typeof setTimeout> | null = null
  let clipboardTimer: ReturnType<typeof setTimeout> | null = null
  let toastTimer: ReturnType<typeof setTimeout> | null = null
  let toastHolder: HTMLElement | null = null
  let hiddenSince: number | null = null
  let lastFocus: HTMLElement | null = null
  let destroyed = false

  /**
   * "Open a different vault with my security key", reachable from every screen.
   *
   * The defect this exists to kill: a machine adopted to vault A, holding the key for vault B, had
   * no route to B. The only exit offered was "start a new vault", dressed as a threat — so the one
   * safe action looked like the dangerous one and the dangerous one looked like the only one.
   *
   * Nothing here is destructive. Adoptions are a set, so opening B leaves A's blob untouched, and
   * nothing on the server changes. There is therefore no warning to give.
   */
  const openOtherButton = (label = "Open a different vault"): HTMLElement => {
    const button = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [label])
    button.addEventListener("click", () => {
      error = null
      screen = "adopt"
      render()
    })
    return button
  }

  const nameCache = deps.names ?? createIndexedDbNameCache()
  const decoder = new TextDecoder()
  const encoder = new TextEncoder()

  /**
   * Reads back the vault names this password sealed. Entries sealed under a different password
   * simply do not open; that is not an error, it is a partial list, and the screen says so.
   */
  async function readNames(password: string): Promise<Map<string, string>> {
    const found = new Map<string, string>()
    let stored: SealedNames[] = []
    try {
      stored = await nameCache.read()
    } catch {
      return found
    }
    for (const entry of stored) {
      try {
        const plain = await openLocal({ passphrase: password, kdf: entry.kdf, sealed: entry.sealed })
        for (const [id, name] of Object.entries(
          JSON.parse(decoder.decode(plain)) as Record<string, string>,
        )) {
          found.set(id, name)
        }
      } catch {
        /* sealed under another password — nothing to report, and nothing to fix */
      }
    }
    return found
  }

  /**
   * Adds one name to whichever sealed group this password opens, or starts a new group.
   *
   * Never throws. The cache is a convenience — the name is always retypeable and the key always
   * finds the vault — so a private-mode window or a blocked IndexedDB must not be able to fail a
   * vault that was otherwise created successfully.
   */
  async function rememberName(password: string, vaultId: string, display: string): Promise<void> {
    try {
      await writeName(password, vaultId, display)
    } catch {
      /* the list will be unnamed on this machine; nothing else is affected */
    }
  }

  async function writeName(password: string, vaultId: string, display: string): Promise<void> {
    const entries = await nameCache.read()
    for (const [index, entry] of entries.entries()) {
      let parsed: Record<string, string>
      try {
        const plain = await openLocal({ passphrase: password, kdf: entry.kdf, sealed: entry.sealed })
        parsed = JSON.parse(decoder.decode(plain)) as Record<string, string>
      } catch {
        continue
      }
      parsed[vaultId] = display
      entries[index] = {
        kdf: entry.kdf,
        sealed: await sealLocal({
          passphrase: password,
          kdf: entry.kdf,
          plaintext: encoder.encode(JSON.stringify(parsed)),
        }),
      }
      await nameCache.write(entries)
      return
    }
    const kdf = generateKdfParams()
    entries.push({
      kdf,
      sealed: await sealLocal({
        passphrase: password,
        kdf,
        plaintext: encoder.encode(JSON.stringify({ [vaultId]: display })),
      }),
    })
    await nameCache.write(entries)
  }

  /** A new vault costs a name, not a slot on the key — so this is an ordinary action, not a risk. */
  const createAnotherButton = (): HTMLElement => {
    const button = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
      "Create another vault",
    ])
    button.addEventListener("click", () => {
      error = null
      screen = "welcome"
      render()
    })
    return button
  }

  const seenVault = (): boolean => {
    try {
      return storage?.getItem(SEEN_VAULT_KEY) === "1"
    } catch {
      return false
    }
  }
  const forgetSeen = (): void => {
    try {
      storage?.removeItem(SEEN_VAULT_KEY)
      storage?.removeItem(CREDENTIAL_KEY)
      storage?.removeItem(CREDENTIAL_PK_KEY)
      storage?.removeItem(VAULT_ID_KEY)
    } catch {
      /* nothing to do; these are only hints */
    }
  }

  /** Show the unlock password unless this user has previously chosen to hide it. */
  const unlockRevealed = (): boolean => {
    try {
      return storage?.getItem(REVEAL_KEY) !== "0"
    } catch {
      return true
    }
  }
  const rememberReveal = (revealed: boolean): void => {
    try {
      if (revealed) storage?.removeItem(REVEAL_KEY)
      else storage?.setItem(REVEAL_KEY, "0")
    } catch {
      /* a preference, not a requirement */
    }
  }

  /**
   * Remembering which credential opened this vault is what removes the browser's chooser. A key
   * can hold several discoverable credentials, and a browser will happily list a synced passkey
   * next to a security key — picking the wrong one fails in a way that reads like a broken vault
   * rather than a wrong choice. None of this is secret: a credential id is a public handle.
   */
  const rememberCredential = (id: Uint8Array | null, vaultId: string | null): void => {
    try {
      if (id) storage?.setItem(CREDENTIAL_KEY, b64u(id))
      if (vaultId) storage?.setItem(VAULT_ID_KEY, vaultId)
    } catch {
      /* private mode: the chooser comes back, which still works */
    }
  }

  /**
   * The credential this machine created a vault with, kept so a *second* vault can reuse it.
   *
   * Without this every new vault would spend another discoverable-credential slot, which is the
   * cost the whole naming design exists to avoid (spec §8.2). Only a machine that has enrolled
   * knows it: an assertion never returns a public key, so a machine that merely adopted will
   * still create a new credential.
   */
  const rememberEnrolCredential = (credential: {
    credentialId: Uint8Array
    publicKey: Uint8Array
  }): void => {
    try {
      storage?.setItem(CREDENTIAL_KEY, b64u(credential.credentialId))
      storage?.setItem(CREDENTIAL_PK_KEY, b64u(credential.publicKey))
    } catch {
      /* private mode: the next vault costs a slot, which still works */
    }
  }

  const reusableCredential = ():
    | { credentialId: Uint8Array; publicKey: Uint8Array }
    | undefined => {
    try {
      const id = storage?.getItem(CREDENTIAL_KEY)
      const pk = storage?.getItem(CREDENTIAL_PK_KEY)
      if (!id || !pk) return undefined
      const credentialId = unb64u(id)
      const publicKey = unb64u(pk)
      // A half-decodable pair is a corrupted hint, not a credential. Falling back to creating one
      // costs a slot; sending malformed bytes into a ceremony would fail in a way that reads like
      // a broken key.
      return credentialId && publicKey ? { credentialId, publicKey } : undefined
    } catch {
      return undefined
    }
  }

  const preferredCredential = (): Uint8Array | undefined => {
    try {
      const stored = storage?.getItem(CREDENTIAL_KEY)
      return stored ? unb64u(stored) : undefined
    } catch {
      return undefined
    }
  }

  const knownVaultId = (): string | null => {
    try {
      return storage?.getItem(VAULT_ID_KEY) ?? null
    } catch {
      return null
    }
  }
  const markSeen = (): void => {
    try {
      storage?.setItem(SEEN_VAULT_KEY, "1")
    } catch {
      /* private mode; the welcome screen is a harmless fallback */
    }
  }

  // Which screen depends on whether this machine has been adopted, which is an async read. Start
  // on "loading" and settle once it resolves.
  screen = deps.session.state === "unlocked" ? "vault" : "loading"
  void adoptions
    .list()
    .then((found) => {
      adoptionList = found
      // One adoption goes straight to the password field: §7.3 is the everyday path and must not
      // grow a click. What fixes a machine holding the wrong vault is the door on every screen,
      // not a list — see `openOtherButton`.
      adoption = found.length === 1 ? found[0]! : null
      if (deps.session.state !== "unlocked") {
        screen =
          found.length > 1
            ? "vaults"
            : found.length === 1
              ? "locked"
              : seenVault()
                ? "adopt"
                : "welcome"
      }
      render()
    })
    .catch(() => {
      screen = seenVault() ? "adopt" : "welcome"
      render()
    })

  // --- helpers ------------------------------------------------------------------------------

  function paintToast(): void {
    if (!toastHolder) return
    toastHolder.replaceChildren(
      ...(toast ? [el("div", { class: "toast", role: "status" }, [toast])] : []),
    )
  }

  /**
   * Toasts appear and expire in place. The expiry used to call `render()`, which rebuilt the whole
   * screen from stored state two seconds after the fact — so pressing "Suggest one" filled the
   * password fields and then silently emptied them again, and Copy in the entry editor discarded
   * whatever had been typed since the modal opened.
   *
   * It also meant the toast was invisible unless something else happened to render: `showToast`
   * set the text but never painted it. Both symptoms, one cause.
   */
  function showToast(message: string): void {
    toast = message
    paintToast()
    if (toastTimer) clearTimeout(toastTimer)
    toastTimer = setTimeout(() => {
      toast = null
      toastTimer = null
      paintToast()
    }, 2200)
  }

  async function attempt(fn: () => Promise<void>): Promise<void> {
    busy = true
    error = null
    render()
    try {
      await fn()
    } catch (err) {
      error = describeError(err)
    } finally {
      busy = false
      render()
    }
  }

  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      saveTimer = null
      void attempt(async () => {
        await deps.session.save()
        showToast("Saved")
      })
    }, saveDebounceMs)
  }

  async function copyValue(value: string, label: string): Promise<void> {
    if (!clipboard) return
    await clipboard.writeText(value)
    showToast(`${label} copied — clears in ${Math.round(clipboardClearMs / 1000)}s`)
    if (clipboardTimer) clearTimeout(clipboardTimer)
    clipboardTimer = setTimeout(() => {
      clipboardTimer = null
      void (async () => {
        try {
          // Only clear what we wrote: the user may have copied something else since, and
          // destroying that would be worse than leaving this.
          const current = await clipboard.readText?.()
          if (current !== undefined && current !== value) return
          await clipboard.writeText("")
        } catch {
          /* permission refused; nothing we can do */
        }
      })()
    }, clipboardClearMs)
  }

  function lock(reason: LockReason = "explicit"): void {
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    editing = null
    revealed = false
    query = ""
    deps.session.lock(reason)
    screen = "locked"
    // Clear the DOM before anything else, so decrypted values do not survive in detached nodes.
    root.replaceChildren()
    render()
  }

  // --- screens ------------------------------------------------------------------------------

  const brand = () =>
    el("div", { class: "brand" }, [
      el("div", { class: "brand-mark" }),
      el("div", { class: "brand-name" }, ["Vault"]),
    ])

  /**
   * The welcome screen's brand, where the mark doubles as an About control.
   *
   * The explanation used to sit permanently between the brand and the password fields. A form
   * this short does not need a preamble, and the preamble had also gone stale — it still claimed
   * the app generates the passphrase. It is one click away instead, and easy to reword later.
   *
   * The name is the page's `h1` so the screen still has a heading for a screen reader; the CSS
   * keeps it looking exactly as it did.
   */
  function brandAbout(): { node: Node; panel: HTMLElement } {
    const panel = el("div", { class: "about", id: "about", hidden: true }, [
      el("p", {}, [
        "Your entries are encrypted in this browser before they leave it, and stored as an opaque " +
          "blob. Whoever holds that blob cannot read it — not the server, not us.",
      ]),
      el("p", {}, [
        "Creating a vault needs your security key once. After that, this computer opens it with " +
          "your password alone.",
      ]),
    ])

    const mark = el("button", {
      class: "brand-mark",
      type: "button",
      "aria-label": "About this app",
      "aria-expanded": "false",
      "aria-controls": "about",
    })
    mark.addEventListener("click", () => {
      const opening = panel.hasAttribute("hidden")
      // In place, never a render: this screen holds two password fields the user may have filled.
      if (opening) panel.removeAttribute("hidden")
      else panel.setAttribute("hidden", "")
      mark.setAttribute("aria-expanded", opening ? "true" : "false")
    })

    return {
      node: el("div", { class: "brand" }, [
        mark,
        el("h1", { class: "brand-name" }, ["Vault"]),
      ]),
      panel,
    }
  }

  /**
   * Messages live in a container the screen keeps a handle on, so they can be shown and cleared
   * without a `render()`. A re-render would rebuild the screen from stored state and discard
   * whatever the user has typed — which is how a failed confirmation used to wipe both password
   * fields on the way to complaining about them.
   */
  function paintNotices(): void {
    if (!noticeHolder) return
    const out: Node[] = []
    if (error) out.push(el("div", { class: "notice notice-error", role: "alert" }, [error]))
    if (warning) out.push(el("div", { class: "notice notice-warn", role: "alert" }, [warning]))
    noticeHolder.replaceChildren(...out)
  }

  /** The notice area for the screen currently being built. Each screen embeds exactly one. */
  function notices(): Node {
    noticeHolder = el("div", { class: "notices" })
    paintNotices()
    return noticeHolder
  }

  /** Show a message in place. Falls back to a render only if no screen is holding a notice area. */
  function setError(message: string | null): void {
    error = message
    if (noticeHolder?.isConnected) paintNotices()
    else render()
  }

  /** As `setError`, for the softer kind: something the user may do anyway once they have read it. */
  function setWarning(message: string | null): void {
    warning = message
    if (noticeHolder?.isConnected) paintNotices()
    else render()
  }

  /**
   * Any click or keystroke dismisses what is on screen. An error the user has already seen and is
   * acting on should not sit there arguing with them while they fix it.
   */
  function clearNotices(): void {
    if (!error && !warning) return
    error = null
    warning = null
    if (noticeHolder?.isConnected) paintNotices()
    else render()
  }

  function welcomeScreen(): Node {
    const about = brandAbout()
    // Rebuilt screen, fresh button label: the confirmation must be earned again.
    warnedFor = null

    // Shown, not masked (spec §0). A password you cannot see is one you cannot check, and this is
    // the one moment the user is inventing it rather than recalling it.
    const passwordField = secretField(
      { id: "new-password", autocomplete: "new-password" },
      { revealed: true },
    )
    const confirmField = secretField(
      { id: "confirm-password", autocomplete: "new-password" },
      { revealed: true },
    )
    const password = passwordField.input
    const confirm = confirmField.input
    const nameInput = el("input", { type: "text", id: "new-name", autocomplete: "off" })

    // Sits directly under the field it fills, not in the row at the bottom — down there it read
    // as a third top-level action and gave no clue which field it was about.
    const suggest = el("button", { class: "btn btn-ghost btn-xs", type: "button" }, ["Suggest one"])
    suggest.addEventListener("click", () => {
      // 5 is the generator's floor; ~65 bits, and short enough to type.
      const generated = generatePassphrase({ words: 5 })
      password.value = generated
      // Only the first field. Typing it into the second is how the user learns the phrase and
      // proves they have it written down — filling both would make the confirmation a formality.
      confirm.value = ""
      // Not a re-render: setting the value above would be lost by one. Sync the toggle so the eye
      // matches the state it is being put into.
      passwordField.setRevealed(true)
      confirm.focus()
      showToast("Suggested — write it down, then type it below")
    })

    const create = el("button", { class: "btn btn-primary", type: "button" }, ["Create a vault"])
    create.addEventListener("click", () => {
      // `setError`, never `render()`: rebuilding this screen would throw away both passwords on
      // the way to telling the user they do not match, which is the least helpful moment to do it.
      const chosen = password.value
      if (chosen !== confirm.value) {
        setError("The two passwords do not match.")
        confirm.focus()
        confirm.select()
        return
      }
      // Spec §0. A weak password is the user's call to make; ours is to be sure they are making
      // it rather than stumbling into it. So: name the actual consequence once, in their terms,
      // and let the next press through. The warning is re-armed if they change the password, so
      // confirming one weak password does not silently confirm a different one.
      const floor = meetsFloor(chosen)
      if (!floor.ok && warnedFor !== chosen) {
        warnedFor = chosen
        setWarning(
          floor.reason === "too-short"
            ? `That is ${chosen.length} character${chosen.length === 1 ? "" : "s"}. Anyone who ` +
              "gets hold of this computer could open your vault by guessing — your security key " +
              "does not stop them, because this password is the part that is meant to. " +
              `I suggest ${MIN_PASSWORD_LENGTH} or more, or press Suggest one. ` +
              "Press Create it anyway if you want it regardless."
            : floor.reason === "too-common"
              ? "That password is on every list an attacker tries first, so it would be among " +
                "the earliest guesses rather than a lucky one. Press Create it anyway if you " +
                "want it regardless."
              : "You have not typed a password. The vault would then open on this computer with " +
                "no password at all — anyone using it could read every entry. Press Create it " +
                "anyway if that is what you want.",
        )
        create.textContent = "Create it anyway"
        password.focus()
        return
      }

      void attempt(async () => {
        screen = "enrolling"
        render()
        try {
          // The credential's userName no longer distinguishes vaults — one credential now holds
          // many — so it names the key, and the vault name does the distinguishing (spec §8.2).
          const reuse = reusableCredential()
          const result = await deps.session.enrol({
            name: nameInput.value,
            userName: "vault",
            password: chosen,
            ...(reuse ? { existingCredential: reuse } : {}),
          })
          rememberEnrolCredential(result.credential)
          await adoptions.set(result)
          adoptionList = await adoptions.list()
          await rememberName(chosen, result.vaultId, nameInput.value.trim())
          vaultNames.set(result.vaultId, nameInput.value.trim())
          adoption = result
          rememberCredential(deps.session.credentialId, result.vaultId)
          credentials = {
            recoveryPhrase: result.recoveryPhrase,
            vaultId: result.vaultId,
            name: nameInput.value.trim(),
          }
          confirmedCode = false
          screen = "credentials"
          markSeen()
        } catch (err) {
          // Back to a screen with buttons. The enrolling screen has none by design — it is a
          // progress view — so leaving the user there on a failure strands them at a dead end.
          screen = "welcome"
          throw err
        }
      })
    })

    const unlock = el("button", { class: "btn btn-ghost", type: "button" }, [
      "I already have a vault",
    ])
    unlock.addEventListener("click", () => {
      // Not "locked": without an adoption blob this machine cannot open anything with a password
      // alone, so sending them to a password box would be a dead end.
      screen = adoption ? "locked" : "adopt"
      render()
    })

    return el("div", { class: "centre" }, [
      el("div", { class: "card" }, [
        about.node,
        about.panel,
        notices(),
        el("label", { class: "field", for: "new-name" }, [
          el("span", {}, ["Name this vault"]),
          nameInput,
        ]),
        // The name is how any other machine finds this vault, and it is not recoverable from the
        // key: forgetting it loses the vault even with the key, the password and the recovery
        // phrase in hand. That is why it is written on the recovery card too.
        el("p", { class: "footnote" }, [
          "You will type this name on any other computer or phone to open this vault, so pick " +
            "something you will remember. Capitals, spaces and dashes are ignored. It is not a " +
            "secret \u2014 your password and security key are what protect the vault.",
        ]),
        el("label", { class: "field field-tight", for: "new-password" }, [
          el("span", {}, ["Choose a password"]),
          passwordField.wrap,
        ]),
        el("div", { class: "field-aside" }, [suggest]),
        el("label", { class: "field", for: "confirm-password" }, [
          el("span", {}, ["Confirm it"]),
          confirmField.wrap,
        ]),
        el("p", { class: "footnote" }, [
          `I suggest at least ${MIN_PASSWORD_LENGTH} characters. This is the only thing you type ` +
            "to open the vault, so it must not be one you use elsewhere.",
        ]),
        el("div", { class: "actions" }, [create, unlock]),
      ]),
    ])
  }

  function enrollingScreen(): Node {
    return el("div", { class: "centre" }, [
      el("div", { class: "card" }, [
        brand(),
        el("h1", {}, ["Creating your vault"]),
        el("p", {}, ["Follow the prompts from your security key."]),
        notices(),
      ]),
    ])
  }

  /**
   * A gate, not a notification. These two strings exist in exactly one place — this screen — and
   * everything else in the system is designed so that losing them loses the vault. A screen that
   * can be dismissed with one click will be dismissed with one click.
   */
  /**
   * A gate, not a notification. The recovery code exists in exactly one place — this screen — and
   * it is the only way back in if this machine is lost. A screen dismissible with one click will
   * be dismissed with one click.
   *
   * The password is no longer shown here: the user chose it, so there is nothing to transcribe.
   */
  /**
   * The phrase as a numbered list rather than a run of words.
   *
   * Order is the whole of it — the same eight words in the wrong order open nothing — and a run of
   * words separated by wide gaps is exactly what a screen photograph or an OCR pass reorders when
   * it wraps. Numbering makes the order explicit on the page, in a photograph of the page, and in
   * anything transcribed from either, and lets the user check it themselves.
   */
  function phraseList(phrase: string): Node {
    const words = phrase.split(/\s+/u).filter(Boolean)
    return el(
      "ol",
      { class: "phrase" },
      words.map((word, i) =>
        el("li", {}, [
          el("span", { class: "n", "aria-hidden": "true" }, [String(i + 1)]),
          el("span", { class: "w" }, [word]),
        ]),
      ),
    )
  }

  /** 64 hex characters in blocks of 8, so a line wrap cannot invent or hide a space. */
  function groupHex(value: string): string {
    return (value.match(/.{1,8}/gu) ?? []).join(" ")
  }

  function credentialsScreen(): Node {
    // Copying beats transcribing, and beats photographing the screen — which is what a user does
    // when there is no other way to get a value off it, and which is how a screenshot of a
    // recovery phrase ends up in a photo library forever. Both buttons copy the canonical value,
    // without the grouping spaces the display adds.
    const copyPhrase = el("button", { class: "btn btn-sm", type: "button" }, ["Copy phrase"])
    copyPhrase.addEventListener("click", () => {
      void copyValue(credentials?.recoveryPhrase ?? "", "Recovery phrase")
    })
    const copyVaultId = el("button", { class: "btn btn-sm", type: "button" }, ["Copy vault ID"])
    copyVaultId.addEventListener("click", () => {
      void copyValue(credentials?.vaultId ?? "", "Vault ID")
    })

    const check = el("input", { type: "checkbox", id: "code-written" })
    ;(check as HTMLInputElement).checked = confirmedCode
    const cont = el("button", { class: "btn btn-primary", type: "button", id: "continue" }, [
      "Open my vault",
    ])
    ;(cont as HTMLButtonElement).disabled = !confirmedCode
    check.addEventListener("change", () => {
      confirmedCode = (check as HTMLInputElement).checked
      ;(cont as HTMLButtonElement).disabled = !confirmedCode
    })
    cont.addEventListener("click", () => {
      credentials = null
      confirmedCode = false
      screen = "vault"
      render()
    })

    return el("div", { class: "centre" }, [
      el("div", { class: "card card-wide" }, [
        brand(),
        el("h1", {}, ["Write this down and store it safely"]),
        el("p", {}, [
          "Your vault is ready. From now on this computer opens it with your password alone — no " +
            "security key, no PIN.",
        ]),
        el("p", {}, [
          "The recovery phrase below is your way back in if this computer is lost or wiped and the " +
            "security key is gone too. It is shown once and stored nowhere.",
        ]),

        // The name goes on the card because it is the one thing here that can be forgotten, and
        // forgetting it loses the vault even with key, password and phrase in hand: no machine can
        // enumerate names, so there is nothing to ask (spec §2.2, §7.7b).
        el("div", { class: "field" }, [
          el("span", {}, ["Vault name"]),
          el("div", { class: "secret" }, [credentials?.name ?? ""]),
        ]),
        el("div", { class: "field" }, [
          el("span", {}, ["Recovery phrase"]),
          phraseList(credentials?.recoveryPhrase ?? ""),
          el("div", { class: "actions actions-end" }, [copyPhrase]),
        ]),
        el("div", { class: "field" }, [
          el("span", {}, ["Vault ID"]),
          el("div", { class: "secret secret-code" }, [groupHex(credentials?.vaultId ?? "")]),
          el("div", { class: "actions actions-end" }, [copyVaultId]),
        ]),
        el("p", { class: "footnote" }, [
          "Write the name down too: it is how any other computer or phone finds this vault, and " +
            "nothing can look it up for you. " +
            "Recovery asks for both of the values below. Keep them together, somewhere that is " +
            "not this computer. " +
            "The numbers are the word order — recovery needs the words in exactly that order. " +
            "Spacing and capitals do not matter when you type them back.",
        ]),

        el("label", { class: "checkline", for: "code-written" }, [
          check,
          el("span", {}, ["I have written both down and stored them somewhere safe and offline."]),
        ]),
        el("div", { class: "actions actions-end" }, [cont]),
      ]),
    ])
  }

  /**
   * Several vaults are adopted here, so one has to be chosen before a password opens anything.
   *
   * The names are sealed (§3.6 pattern), so this shows a count and a password field first and the
   * names only after. That is deliberate rather than awkward: the adversary this system has is
   * someone holding the laptop (§1.4), and a plain list of vault names is exactly what they would
   * read off it.
   */
  function vaultsScreen(): Node {
    const field = secretField(
      { id: "list-password", autocomplete: "off", spellcheck: "false" },
      { revealed: unlockRevealed(), onToggle: rememberReveal },
    )

    const reveal = () => {
      const typed = field.input.value
      void attempt(async () => {
        vaultNames = await readNames(typed)
        listPassword = typed
        render()
      })
    }

    const show = el("button", { class: "btn btn-primary", type: "button" }, [
      busy ? "Opening\u2026" : "Show my vaults",
    ])
    ;(show as HTMLButtonElement).disabled = busy
    show.addEventListener("click", reveal)
    field.input.addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key === "Enter") reveal()
    })

    const named = adoptionList.filter((a) => vaultNames.has(a.vaultId))

    const rows = named.map((entry) => {
      const button = el("button", { class: "btn btn-primary btn-block", type: "button" }, [
        vaultNames.get(entry.vaultId)!,
      ])
      button.addEventListener("click", () => {
        void attempt(async () => {
          if (listPassword === null) throw new SessionLikeError("type your password first")
          await deps.session.unlock({
            vaultId: entry.vaultId,
            slotId: entry.slotId,
            password: listPassword,
            sealed: entry.sealed,
          })
          adoption = entry
          markSeen()
          screen = "vault"
        })
      })
      return button
    })

    // Having said another vault is here under a different password, the screen has to let that
    // password be typed. It also has to, when the first one opened nothing at all — otherwise the
    // only way out of a mistyped password is a reload.
    //
    // This clears the revealed names rather than accumulating them: each entry is opened with the
    // password that revealed it, so a merged list would offer vaults the password in hand cannot
    // open, and clicking one would fail as if the vault were broken.
    const retry = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
      "Use a different password",
    ])
    retry.addEventListener("click", () => {
      error = null
      listPassword = null
      vaultNames = new Map()
      render()
    })

    const unopened = adoptionList.length - named.length

    return el("div", { class: "centre" }, [
      el("div", { class: "card" }, [
        brand(),
        el("h1", {}, ["Your vaults"]),
        notices(),
        ...(listPassword === null
          ? [
              el("p", {}, [
                `${adoptionList.length} vaults are set up on this computer. Your password shows ` +
                  "which ones, and opens the one you pick.",
              ]),
              el("label", { class: "field", for: "list-password" }, [
                el("span", {}, ["Password"]),
                field.wrap,
              ]),
              el("div", { class: "actions" }, [show]),
            ]
          : [
              ...(named.length > 0
                ? [el("p", {}, ["Pick the one you want."]), ...rows]
                : [
                    el("p", {}, [
                      "That password does not open any of the vaults set up here. Check it, or " +
                        "use your security key.",
                    ]),
                  ]),
              // Said plainly rather than hidden: vaults sealed under a different password are
              // still here, and pretending otherwise would look like data loss.
              ...(unopened > 0 && named.length > 0
                ? [
                    el("p", { class: "footnote" }, [
                      `${unopened} more ${unopened === 1 ? "vault is" : "vaults are"} set up here ` +
                        "under a different password.",
                    ]),
                  ]
                : []),
              el("div", { class: "actions" }, [retry]),
            ]),
        el("div", { class: "actions" }, [
          openOtherButton("Open a vault with my security key"),
          createAnotherButton(),
        ]),
      ]),
    ])
  }

  function lockedScreen(): Node {
    // The one field typed every single day. Shown by default (spec §0); if this user hides it
    // once, it stays hidden — the preference is theirs to set, not ours to impose either way.
    const field = secretField(
      { id: "passphrase", autocomplete: "off", spellcheck: "false" },
      { revealed: unlockRevealed(), onToggle: rememberReveal },
    )
    const input = field.input

    const submit = () => {
      const value = input.value
      void attempt(async () => {
        if (!adoption) throw new SessionLikeError("this computer has not been set up")
        await deps.session.unlock({
          vaultId: adoption.vaultId,
          slotId: adoption.slotId,
          password: value,
          sealed: adoption.sealed,
        })
        markSeen()
        // Not awaited: unsealing the names costs a PBKDF2 and the vault is already open. The
        // header fills in a moment later rather than holding the everyday path up for a label.
        void readNames(value).then((found) => {
          if (found.size === 0) return
          vaultNames = new Map([...vaultNames, ...found])
          render()
        })
        screen = "vault"
      })
    }

    const unlock = el("button", { class: "btn btn-primary", type: "button" }, [
      busy ? "Unlocking…" : "Unlock",
    ])
    ;(unlock as HTMLButtonElement).disabled = busy
    unlock.addEventListener("click", submit)
    input.addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key === "Enter") submit()
    })

    const recover = el("button", { class: "btn btn-ghost", type: "button" }, [
      "Use a recovery phrase",
    ])
    recover.addEventListener("click", () => {
      screen = "recovery"
      render()
    })

    const startOver = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
      "Can't get in? Start a new vault",
    ])
    startOver.addEventListener("click", () => {
      error = null
      screen = "start-over"
      render()
    })

    return el("div", { class: "centre" }, [
      el("div", { class: "card" }, [
        brand(),
        el("h1", {}, ["Unlock your vault"]),
        el("p", {}, ["Just your password. No security key, no PIN."]),
        notices(),
        el("label", { class: "field", for: "passphrase" }, [
          el("span", {}, ["Password"]),
          field.wrap,
        ]),
        el("div", { class: "actions" }, [unlock, recover]),
        // Both doors belong here, not only on the list. A machine with exactly one vault lands on
        // this screen, so putting "create another" only on `vaults` meant you needed two vaults to
        // reach the screen that makes a second one — leaving "start a new vault", which forgets
        // this one, as the only thing that looked like it would work.
        el("div", { class: "actions" }, [openOtherButton(), createAnotherButton()]),
        ...(knownVaultId()
          ? [
              el("p", { class: "footnote mono" }, [
                `Vault ${knownVaultId()!.slice(0, 12)}\u2026`,
                preferredCredential() ? " \u00b7 remembered security key" : "",
              ]),
            ]
          : []),
        el("div", { class: "actions" }, [startOver]),
      ]),
    ])
  }

  /**
   * The way out when recovery is impossible — a lost key, a forgotten passphrase, a lost card.
   *
   * It abandons the old vault; it does not delete it. Nothing here can: deleting would need the
   * credentials that are missing, which is the whole problem. The old ciphertext stays on the
   * server, unreadable by anyone including us, and the entries in it are gone for good. Saying
   * that plainly matters more than making the button easy.
   */
  function startOverScreen(): Node {
    const check = el("input", { type: "checkbox", id: "understand" })
    const proceed = el("button", { class: "btn btn-primary", type: "button", id: "start-fresh" }, [
      "Create a new empty vault",
    ])
    ;(proceed as HTMLButtonElement).disabled = true
    check.addEventListener("change", () => {
      ;(proceed as HTMLButtonElement).disabled = !(check as HTMLInputElement).checked
    })
    proceed.addEventListener("click", () => {
      void attempt(async () => {
        forgetSeen()
        // Only the vault that cannot be opened. Clearing the whole set would forget vaults this
        // machine still opens perfectly well — harmless to the data, but it would send the user
        // back through a hardware ceremony for each one.
        if (adoption) await adoptions.delete(adoption.vaultId)
        else await adoptions.clear()
        adoptionList = await adoptions.list()
        adoption = null
        screen = "welcome"
      })
    })

    const back = el("button", { class: "btn btn-ghost", type: "button" }, ["Go back"])
    back.addEventListener("click", () => {
      screen = "locked"
      render()
    })

    // The last off-ramp before something drastic: if the key is in hand, this is not the screen
    // they need. Reaching it by mistake is how the defect of 2026-08-28 played out.
    const openOther = openOtherButton("I have my security key \u2014 open a vault with it")

    return el("div", { class: "centre" }, [
      el("div", { class: "card card-wide" }, [
        brand(),
        el("h1", {}, ["Start a new vault"]),
        el("div", { class: "notice notice-warn" }, [
          "This does not recover your old vault, and it cannot delete it either — deleting would " +
            "need the passphrase and key you no longer have.",
        ]),
        el("p", {}, [
          "Everything in the old vault stays encrypted on the server, unreadable by anyone. " +
            "Its entries are gone for good.",
        ]),
        el("p", {}, [
          "You will create a new, empty vault under a new name. It can live on the security key " +
            "you already have \u2014 a vault costs a name, not a slot on the key \u2014 and the old " +
            "vault's name simply stops being used.",
        ]),
        el("label", { class: "checkline", for: "understand" }, [
          check,
          el("span", {}, ["I understand the entries in my old vault are permanently lost."]),
        ]),
        el("div", { class: "actions" }, [proceed, back]),
        el("div", { class: "actions" }, [openOther]),
      ]),
    ])
  }

  /**
   * A machine that has never opened this vault. One hardware-key ceremony teaches it the secret,
   * after which the password alone is enough here too.
   */
  function adoptScreen(): Node {
    const nameInput = el("input", { type: "text", id: "adopt-name", autocomplete: "off" })
    const passField = secretField(
      { id: "adopt-password", autocomplete: "off" },
      { revealed: true },
    )
    const pass = passField.input

    const go = el("button", { class: "btn btn-primary", type: "button", id: "adopt" }, [
      // Names the thing the user must physically do, not the outcome \u2014 the heading already says
      // what the screen is for, and the outcome is not what they need to know at the moment of
      // pressing. This is also the only button here that wants the key in the slot.
      busy ? "Setting up\u2026" : "Use a security key",
    ])
    ;(go as HTMLButtonElement).disabled = busy
    go.addEventListener("click", () => {
      void attempt(async () => {
        const adopted = await deps.session.adopt({
          name: nameInput.value,
          password: pass.value,
        })
        await adoptions.set(adopted)
        adoptionList = await adoptions.list()
        await rememberName(pass.value, adopted.vaultId, nameInput.value.trim())
        vaultNames.set(adopted.vaultId, nameInput.value.trim())
        adoption = adopted
        adoption = adopted
        rememberCredential(deps.session.credentialId, adopted.vaultId)
        markSeen()
        showToast("Done \u2014 from now on this computer needs only your password")
        screen = "vault"
      })
    })

    const recover = el("button", { class: "btn btn-ghost", type: "button" }, ["Use a recovery phrase"])
    recover.addEventListener("click", () => {
      error = null
      screen = "recovery"
      render()
    })

    const fresh = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
      "Start a new vault instead",
    ])
    fresh.addEventListener("click", () => {
      error = null
      screen = "start-over"
      render()
    })

    return el("div", { class: "centre" }, [
      el("div", { class: "card" }, [
        brand(),
        el("h1", {}, ["Set up this computer"]),
        el("p", {}, [
          "Insert your security key, name the vault you want, and enter your password. You will " +
            "be asked to touch the key once. After this, opening it on this computer needs only " +
            "the password.",
        ]),
        notices(),
        el("label", { class: "field", for: "adopt-name" }, [
          el("span", {}, ["Vault name"]),
          nameInput,
        ]),
        // Spacing, case and dashes are ignored, so "My Crypto" and "mycrypto" are one vault. Said
        // here rather than after a failure, because a mistyped name is indistinguishable from a
        // vault that does not exist.
        el("p", { class: "hint" }, [
          "Capitals, spaces and dashes do not matter. The name must otherwise match exactly.",
        ]),
        el("label", { class: "field", for: "adopt-password" }, [
          el("span", {}, ["Password"]),
          passField.wrap,
        ]),
        el("div", { class: "actions" }, [go, recover]),
        el("div", { class: "actions" }, [fresh]),
      ]),
    ])
  }

  function recoveryScreen(): Node {
    const vaultId = el("input", { type: "text", id: "vault-id", class: "mono", autocomplete: "off" })
    const phrase = el("input", { type: "text", id: "rec-phrase", autocomplete: "off" })
    // The phrase field beside this one has always been plain text; there was never a reason for
    // the password to be the odd one out.
    const passField = secretField({ id: "rec-pass", autocomplete: "off" }, { revealed: true })
    const pass = passField.input

    const go = el("button", { class: "btn btn-primary", type: "button" }, ["Recover vault"])
    ;(go as HTMLButtonElement).disabled = busy
    go.addEventListener("click", () => {
      void attempt(async () => {
        const adopted = await deps.session.unlockWithRecoveryPhrase({
          // Every space, not just the ends. The ID is displayed in blocks of 8 and wraps on a
          // narrow screen, so a copy — or a transcription from a photograph — routinely arrives
          // with spaces in the middle. Hex has no meaningful whitespace and no case, so both are
          // noise: rejecting on them would fail a value the user copied correctly.
          vaultId: (vaultId as HTMLInputElement).value.replace(/\s+/gu, "").toLowerCase(),
          phrase: (phrase as HTMLInputElement).value,
          password: pass.value,
        })
        await adoptions.set(adopted)
        adoptionList = await adoptions.list()
        adoption = adopted
        markSeen()
        showToast("This computer now opens with your password alone")
        screen = "vault"
      })
    })

    const back = el("button", { class: "btn btn-ghost", type: "button" }, ["Back"])
    back.addEventListener("click", () => {
      error = null
      screen = "locked"
      render()
    })

    return el("div", { class: "centre" }, [
      el("div", { class: "card card-wide" }, [
        brand(),
        el("h1", {}, ["Recover with your phrase"]),
        el("p", {}, [
          "Use this if your security keys are lost. You still need your password, and the vault ID " +
            "written down alongside the phrase.",
        ]),
        notices(),
        el("label", { class: "field", for: "vault-id" }, [el("span", {}, ["Vault ID"]), vaultId]),
        el("label", { class: "field", for: "rec-phrase" }, [el("span", {}, ["Recovery phrase"]), phrase]),
        el("label", { class: "field", for: "rec-pass" }, [
          el("span", {}, ["Passphrase"]),
          passField.wrap,
        ]),
        el("div", { class: "actions" }, [go, back]),
        el("p", { class: "footnote" }, [
          "Lost the phrase as well? Go back and choose \u201cStart a new vault\u201d. The old vault " +
            "cannot be opened or deleted without its passphrase and key.",
        ]),
      ]),
    ])
  }

  function vaultScreen(): Node {
    const store = deps.session.store()
    const entries = store.list({ query })

    const search = el("input", {
      type: "search",
      class: "search",
      id: "search",
      placeholder: "Search entries…   /",
      "aria-label": "Search entries",
      value: query,
    })
    search.addEventListener("input", () => {
      query = (search as HTMLInputElement).value
      renderList()
    })

    const add = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["New entry"])
    add.addEventListener("click", () => {
      lastFocus = add as HTMLElement
      editing = "new"
      render()
    })

    const addKey = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Add a key"])
    addKey.addEventListener("click", () => {
      // The name is asked for rather than remembered: a session opened by password alone (§7.3)
      // never learned one, and keeping it would put the vault's name in the clear on this machine.
      const name = prompt("Which vault is this? Type its name.")
      if (!name) return
      const password = prompt("Confirm your password to enrol another security key")
      if (!password) return
      void attempt(async () => {
        await deps.session.addKey({
          name,
          password,
          label: `key ${new Date().toISOString().slice(0, 10)}`,
        })
        showToast("Second key enrolled")
      })
    })

    const lockButton = el("button", { class: "btn btn-sm", type: "button" }, ["Lock"])
    lockButton.addEventListener("click", () => lock("explicit"))

    const listHolder = el("div", { id: "list" })

    function renderList(): void {
      const filtered = deps.session.store().list({ query })
      listHolder.replaceChildren(
        el("div", { class: "list-head" }, [
          el("h2", {}, ["Entries"]),
          el("span", { class: "count" }, [
            `${filtered.length} of ${deps.session.store().count()}`,
          ]),
        ]),
        filtered.length === 0
          ? el("div", { class: "empty" }, [
              query ? "No entries match that search." : "No entries yet.",
            ])
          : el(
              "ul",
              { class: "entries" },
              filtered.map((entry) => entryRow(entry)),
            ),
      )
    }

    function entryRow(entry: Entry): Node {
      const copyUser = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
        el("span", { class: "label" }, ["Copy user"]),
      ])
      copyUser.addEventListener("click", (event) => {
        event.stopPropagation()
        void copyValue(entry.username, "Username")
      })

      const copyPass = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [
        el("span", { class: "label" }, ["Copy password"]),
      ])
      copyPass.addEventListener("click", (event) => {
        event.stopPropagation()
        void copyValue(entry.password, "Password")
      })

      const row = el("li", { class: "entry", tabindex: "0", role: "button" }, [
        el("div", { class: "entry-main" }, [
          el("div", { class: "entry-title" }, [entry.title || "Untitled"]),
          el("div", { class: "entry-sub" }, [entry.username || entry.url || "—"]),
        ]),
        el(
          "div",
          { class: "tags" },
          entry.tags.slice(0, 2).map((tag) => el("span", { class: "tag" }, [tag])),
        ),
        el("div", { class: "entry-actions" }, [copyUser, copyPass]),
      ])
      const open = () => {
        lastFocus = row as HTMLElement
        editing = entry
        render()
      }
      row.addEventListener("click", open)
      row.addEventListener("keydown", (event) => {
        const key = (event as KeyboardEvent).key
        if (key === "Enter" || key === " ") {
          event.preventDefault()
          open()
        }
      })
      return row
    }

    renderList()

    // Which vault this is. With several openable by password alone, an unlabelled editor is how
    // an entry lands in the wrong vault — and nothing afterwards would say so.
    const openName = deps.session.vaultId ? vaultNames.get(deps.session.vaultId) : undefined

    return el("div", { class: "shell" }, [
      el("header", { class: "topbar" }, [
        brand(),
        ...(openName ? [el("span", { class: "vault-name" }, [openName])] : []),
        search,
        el("div", { class: "spacer" }),
        add,
        addKey,
        lockButton,
      ]),
      el("main", { class: "content" }, [notices(), listHolder]),
    ])
  }

  function editorModal(): Node {
    const current = editing === "new" ? null : (editing as Entry)
    const value = (key: keyof Entry): string =>
      current ? String(current[key] ?? "") : ""

    const title = el("input", { type: "text", id: "f-title", value: value("title"), autocomplete: "off" })
    const username = el("input", { type: "text", id: "f-user", value: value("username"), autocomplete: "off" })
    // The one field that stays masked by default, and it is not an exception to spec §0: this
    // shows a *stored* secret on a screen left open beside other entries, rather than something
    // the user is typing. Revealing is one click, and resets between openings and on lock.
    const passwordField = secretField(
      { id: "f-pass", value: value("password"), autocomplete: "off" },
      {
        revealed,
        onToggle: (next) => {
          revealed = next
        },
      },
    )
    const password = passwordField.input
    const url = el("input", { type: "text", id: "f-url", value: value("url"), autocomplete: "off" })
    const notes = el("textarea", { id: "f-notes", autocomplete: "off" })
    ;(notes as HTMLTextAreaElement).value = value("notes")
    const tags = el("input", {
      type: "text",
      id: "f-tags",
      value: current ? current.tags.join(", ") : "",
      autocomplete: "off",
    })

    const copy = el("button", { class: "btn btn-sm", type: "button" }, ["Copy"])
    copy.addEventListener("click", () => {
      void copyValue(password.value, "Password")
    })

    const close = (): void => {
      editing = null
      revealed = false
      render()
      lastFocus?.focus()
    }

    const save = el("button", { class: "btn btn-primary", type: "button" }, ["Save"])
    save.addEventListener("click", () => {
      const store = deps.session.store()
      store.upsert({
        ...(current ? { id: current.id } : {}),
        title: (title as HTMLInputElement).value,
        username: (username as HTMLInputElement).value,
        password: (password as HTMLInputElement).value,
        url: (url as HTMLInputElement).value,
        notes: (notes as HTMLTextAreaElement).value,
        tags: (tags as HTMLInputElement).value
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      })
      scheduleSave()
      close()
    })

    const cancel = el("button", { class: "btn btn-ghost", type: "button" }, ["Cancel"])
    cancel.addEventListener("click", close)

    const remove = el("button", { class: "btn btn-danger btn-sm", type: "button" }, ["Delete"])
    remove.addEventListener("click", () => {
      if (!current) return
      deps.session.store().remove(current.id)
      scheduleSave()
      close()
    })

    const modal = el("div", { class: "modal", role: "dialog", "aria-modal": "true" }, [
      el("h2", {}, [current ? "Edit entry" : "New entry"]),
      el("label", { class: "field", for: "f-title" }, [el("span", {}, ["Title"]), title]),
      el("label", { class: "field", for: "f-user" }, [el("span", {}, ["Username"]), username]),
      el("label", { class: "field", for: "f-pass" }, [
        el("span", {}, ["Password"]),
        el("div", { class: "reveal-row" }, [passwordField.wrap, copy]),
      ]),
      el("label", { class: "field", for: "f-url" }, [el("span", {}, ["URL"]), url]),
      el("label", { class: "field", for: "f-notes" }, [el("span", {}, ["Notes"]), notes]),
      el("label", { class: "field", for: "f-tags" }, [
        el("span", {}, ["Tags, comma separated"]),
        tags,
      ]),
      el("div", { class: "actions" }, [
        save,
        cancel,
        el("div", { class: "spacer" }),
        ...(current ? [remove] : []),
      ]),
      el("p", { class: "footnote" }, [
        "Copied values clear after 30 seconds. Clipboard history and cross-device sync can keep them anyway.",
      ]),
    ])

    const backdrop = el("div", { class: "backdrop" }, [modal])
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) close()
    })
    backdrop.addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key === "Escape") {
        event.stopPropagation()
        close()
      }
    })
    return backdrop
  }

  // --- render -------------------------------------------------------------------------------

  function render(): void {
    if (destroyed) return
    const children: Node[] = []

    switch (screen) {
      case "welcome":
        children.push(welcomeScreen())
        break
      case "enrolling":
        children.push(enrollingScreen())
        break
      case "credentials":
        children.push(credentialsScreen())
        break
      case "adopt":
        children.push(adoptScreen())
        break
      case "recovery":
        children.push(recoveryScreen())
        break
      case "loading":
        children.push(
          el("div", { class: "centre" }, [el("div", { class: "card" }, [brand(), el("p", {}, ["\u2026"])])]),
        )
        break
      case "start-over":
        children.push(startOverScreen())
        break
      case "vault":
        if (deps.session.state !== "unlocked") {
          screen = "locked"
          children.push(lockedScreen())
        } else {
          children.push(vaultScreen())
          if (editing) children.push(editorModal())
        }
        break
      case "vaults":
        children.push(vaultsScreen())
        break
      case "locked":
        children.push(lockedScreen())
        break
      default:
        children.push(lockedScreen())
    }

    // The toast lives in a holder the render keeps a handle on, so it can appear and expire
    // without another render. See showToast.
    toastHolder = el("div", { class: "toast-host" })
    paintToast()
    children.push(toastHolder)
    root.replaceChildren(...children)

    if (screen === "locked") root.querySelector<HTMLInputElement>("#passphrase")?.focus()
    if (editing) root.querySelector<HTMLInputElement>("#f-title")?.focus()
  }

  // --- global listeners ------------------------------------------------------------------------

  const onInteraction = (): void => {
    if (deps.session.state === "unlocked") deps.session.touch()
    // Dismiss on any click or keystroke. Fires on pointerdown, so a click on the button that
    // failed validation clears the old message before the new one is set.
    clearNotices()
  }

  const onKeydown = (event: KeyboardEvent): void => {
    if (screen !== "vault" || editing) return
    if (event.key === "/" && document.activeElement?.tagName !== "INPUT") {
      event.preventDefault()
      root.querySelector<HTMLInputElement>("#search")?.focus()
    }
  }

  const onVisibility = (): void => {
    if (document.visibilityState === "hidden") {
      hiddenSince = clock()
    } else if (hiddenSince !== null) {
      // Spec §7.7: hidden for more than a minute locks.
      if (clock() - hiddenSince > 60_000 && deps.session.state === "unlocked") lock("idle")
      hiddenSince = null
    }
  }

  const onPageHide = (): void => {
    if (deps.session.state === "unlocked") lock("explicit")
  }

  document.addEventListener("keydown", onKeydown)
  document.addEventListener("keydown", onInteraction)
  document.addEventListener("pointerdown", onInteraction)
  document.addEventListener("visibilitychange", onVisibility)
  globalThis.addEventListener?.("pagehide", onPageHide)

  render()

  return {
    get screen(): ScreenName {
      return screen
    },
    destroy(): void {
      destroyed = true
      if (saveTimer) clearTimeout(saveTimer)
      if (clipboardTimer) clearTimeout(clipboardTimer)
      if (toastTimer) clearTimeout(toastTimer)
      document.removeEventListener("keydown", onKeydown)
      document.removeEventListener("keydown", onInteraction)
      document.removeEventListener("pointerdown", onInteraction)
      document.removeEventListener("visibilitychange", onVisibility)
      globalThis.removeEventListener?.("pagehide", onPageHide)
      root.replaceChildren()
    },
  }
}
